import { LitElement, createRef, css, html, nothing, ref, when, unsafeHTML } from '@lit'
import styles from '@nova/styles'
import { formatPrice, t } from '@nova/utils'

class NovaMiniCart extends LitElement {
  static properties = {
    initialItems: {
      type: Array,
    },
    items: {
      type: Array,
      state: true,
    },
  }

  get count() {
    const count = this.items.reduce(
      (acc, current) => acc + Number.parseInt(current.quantity),
      0,
    )
    return count
  }

  get subtotal() {
    return this.items.reduce(
      (acc, current) =>
        acc + Number.parseFloat(current.final_price) * current.quantity,
      0,
    )
  }

  get formattedSubtotal() {
    return formatPrice(this.subtotal)
  }

  getCompareAtPrice(currentItem) {
    return this.initialItems.find((item) => item.key === currentItem.key)
      ?.compare_at_price
  }

  $cartDrawer = createRef()

  constructor() {
    super()
    document.addEventListener('nova:cart:open', this._handleOpenCart)
    document.addEventListener('nova:cart:close', this._handleCloseCart)
    document.addEventListener('nova:cart:refresh', this.refresh)
    this.initialItems = []
    this.items = []
  }

  connectedCallback() {
    super.connectedCallback()
  }

  disconnectedCallback() {
    document.removeEventListener('nova:cart:open', this._handleOpenCart)
    document.removeEventListener('nova:cart:close', this._handleCloseCart)
    document.removeEventListener('nova:cart:refresh', this.refresh)
  }

  _handleOpenCart = () => {
    this.$cartDrawer.value.show()
  }

  _handleCloseCart = () => {
    this.$cartDrawer.value.hide()
  }

  refresh = async () => {
    try {
      const response = await fetch('/cart.js')
      if (!response.ok) {
        throw new Error('Error fetching cart')
      }
      const data = await response.json()
      this.items = data.items
    } catch {
      console.error('Error fetching cart')
    }
  }

  willUpdate(changedProperties) {
    if (changedProperties.has('initialItems')) {
      this.items = this.initialItems
    }
    if (changedProperties.has('items')) {
      document.dispatchEvent(
        new CustomEvent('nova:cart:items-count', {
          detail: { count: this.count },
        }),
      )
    }
  }

  _renderEmptyState = () => {
    return html`
      <div class="empty-state">
        <p class="empty-title">${t('cart.general.empty')}</p>
        <a class="btn empty-btn" variant="primary-reverse" href=${window.routes.cart_empty_continue_url}>
          ${t('cart.general.continue_shopping')}
        </a>
        ${
          !__NOVA_GLOBAL__.loggedIn
            ? html `
              <div class="flex empty-content">
                <p>${t('cart.general.have_an_account')}</p>
                <p>
                  <a href="/account">Log In</a>${t('cart.general.log_in_to_checkout')}
                </p>
              </div>`
            : nothing
        }
      </div>
    `
  }
  continue_shopping
  _renderItems = () => {
    return html`
      <div slot="label" class="cart-header">
        <h2 class="cart-title">${t('cart.general.title')}</h2>
        ${
          __NOVA_GLOBAL__.showFreeShippingProgressBar === true && Number(__NOVA_GLOBAL__.freeShippingThreshold)
            ? html`<nova-free-shipping-threshold cart-subtotal=${this.subtotal}></nova-free-shipping-threshold>`
            : nothing
        }
      </div>
      <div class="items">
        ${this.items.map(
          (item) =>
            html`
              <nova-cart-item
                @nova-change=${this.refresh}
                .item=${item}
                .compareAtPrice=${this.getCompareAtPrice(item)}>
              </nova-cart-item>
            `,
        )}
      </div>
      <div slot="footer" class="summary">
        <span class="summary__total">
          <p>${t('cart.general.subtotal')}</p>
          <p>${this.formattedSubtotal}</p>
        </span>
        <a class="btn w-full" href="/checkout" variant="primary-reverse">${t('cart.general.checkout')}</a>
        <div class="summary__terms">
          ${unsafeHTML(__NOVA_GLOBAL__.miniCartTermsText)}
        </div>
      </div>
    `
  }

  render() {
    return html`
      <sl-drawer label=${t('cart.general.title')} class="drawer" ${ref(
        this.$cartDrawer,
      )}>
        ${when(!this.items.length, this._renderEmptyState, this._renderItems)}
      </sl-drawer>
    `
  }

  static styles = [
    styles.flex,
    css`
      :host {
        display: block;
      }
      .drawer {
        --size: 100%;
        --body-spacing: 20px 15px;
        --footer-spacing: var(--nova-space-sm) var(--nova-space-md);
      }
      .drawer::part(overlay) {
        background-color: transparent;
      }
      .drawer::part(panel) {
        top: var(--header-height);
        bottom: 0;
        height: auto;
      }
      .drawer::part(header) {
        position: relative;
        border: 1px solid var(--nova-color-grey-200);
      }
      .drawer::part(title) {
        padding: 0;
      }
      .cart-header {
        display: flex;
        flex-direction: column;
        gap: 5px;
      }
      .cart-title {
        font: var(--nova-h5-font);
        letter-spacing: var(--nova-h5-letter-spacing);
        text-transform: capitalize;
        -webkit-font-smoothing: antialiased;
        margin: 0;
      }
      .drawer::part(header-actions) {
        position: absolute;
      }

      .drawer::part(close-button__base) {
        color: black;
        padding: 0;
        font-size: 16px;
      }
      .drawer::part(body) {
        background-color: var(--nova-color-grey-100);
      }
      .drawer::part(footer) {
        box-shadow: 0 -1px 6px 1px rgba(102, 102, 102, 0.10);
        border-top: 1px solid #e4e4e4;
        background: var(--nova-color-grey-100);
      }

      .empty-state {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        height: 100%;
      }
      .empty-title {
        font: var(--nova-h2-font);
        letter-spacing: var(--nova-h2-letter-spacing);
        text-transform: var(--nova-h2-text-transform);
        -webkit-font-smoothing: antialiased;
        margin-bottom: var(--nova-space-sm);
        margin-top: 0;
        text-align: center;
      }
      .summary__total {
        display: flex;
        justify-content: space-between;
        margin-bottom: var(--nova-space-xs);
        font: var(--nova-h6-font);
        letter-spacing: var(--nova-h6-letter-spacing);
        text-transform: var(--nova-h6-text-transform);
        -webkit-font-smoothing: antialiased;
        color: var(--nova-color-black);
      }
      .summary__total p {
        margin: 0;
      }
      .summary__terms {
        color: var(--nova-color-grey-350);
        font: var(--nova-body-caption);
        letter-spacing: var(--nova-caption-letter-spacing);
        text-transform: var(--nova-caption-text-transform);
        -webkit-font-smoothing: antialiased;
        text-align: center;
        margin-bottom: 0;
        margin-top: var(--nova-space-md);
      }

      .summary__terms a {
        color: rgba(var(--color-link), var(--alpha-link));
        transition: text-decoration-thickness var(--duration-short) ease;
        text-underline-offset: 1px;
        text-decoration-thickness: 0.1rem;
        text-decoration: underline;
      }

      .summary__terms ol,
      .summary__terms ul {
        list-style-type: none;
      }

      .summary__terms ul li::before {
        content: '•';
        position: absolute;
        width: 10px;
        transform: translateX(-15px);
      }

      .summary__terms ol {
        counter-reset: item;
      }

      .summary__terms ol li::before {
        counter-increment: item;
        content: counter(item) '.';
        position: absolute;
        transform: translateX(-15px);
      }

      .summary__terms h1 {
        font: var(--nova-h1-font);
        letter-spacing: var(--nova-h1-letter-spacing);
        text-transform: var(--nova-h1-text-transform);
        -webkit-font-smoothing: antialiased;
        /* These variables are used for dynamic font scaling. Learn more here – https://css-tricks.com/linearly-scale-font-size-with-css-clamp-based-on-the-viewport/ */
        --max-font-size-rem: var(--nova-h1-font-size) / var(--nova-base-font-size);
        --min-font-size-rem: var(--nova-h3-font-size) / var(--nova-base-font-size);
        --slope: calc((var(--max-font-size-rem) - var(--min-font-size-rem)) / (var(--nova-font-scaling-max-width-rem) - var(--nova-font-scaling-min-width-rem)));
        --y-axis-intersection: calc(((var(--nova-font-scaling-min-width-rem) * -1) * var(--slope)) + var(--min-font-size-rem));
        --preferred-font-size: calc((var(--y-axis-intersection) * 1rem) + (var(--slope) * 100 * 1vw));
        --dynamic-font-size: round(clamp((var(--min-font-size-rem) * 1rem), var(--preferred-font-size), (var(--max-font-size-rem) * 1rem)), 1px);
      }

      .summary__terms h2 {
        font: var(--nova-h2-font);
        letter-spacing: var(--nova-h2-letter-spacing);
        text-transform: var(--nova-h2-text-transform);
        -webkit-font-smoothing: antialiased;
        /* These variables are used for dynamic font scaling. Learn more here – https://css-tricks.com/linearly-scale-font-size-with-css-clamp-based-on-the-viewport/ */
        --max-font-size-rem: var(--nova-h2-font-size) / var(--nova-base-font-size);
        --min-font-size-rem: var(--nova-h4-font-size) / var(--nova-base-font-size);
        --slope: calc((var(--max-font-size-rem) - var(--min-font-size-rem)) / (var(--nova-font-scaling-max-width-rem) - var(--nova-font-scaling-min-width-rem)));
        --y-axis-intersection: calc(((var(--nova-font-scaling-min-width-rem) * -1) * var(--slope)) + var(--min-font-size-rem));
        --preferred-font-size: calc((var(--y-axis-intersection) * 1rem) + (var(--slope) * 100 * 1vw));
        --dynamic-font-size: round(clamp((var(--min-font-size-rem) * 1rem), var(--preferred-font-size), (var(--max-font-size-rem) * 1rem)), 1px);
      }

      .summary__terms h3 {
        font: var(--nova-h3-font);
        letter-spacing: var(--nova-h3-letter-spacing);
        text-transform: var(--nova-h3-text-transform);
        -webkit-font-smoothing: antialiased;
        /* These variables are used for dynamic font scaling. Learn more here – https://css-tricks.com/linearly-scale-font-size-with-css-clamp-based-on-the-viewport/ */
        --max-font-size-rem: var(--nova-h3-font-size) / var(--nova-base-font-size);
        --min-font-size-rem: var(--nova-h5-font-size) / var(--nova-base-font-size);
        --slope: calc((var(--max-font-size-rem) - var(--min-font-size-rem)) / (var(--nova-font-scaling-max-width-rem) - var(--nova-font-scaling-min-width-rem)));
        --y-axis-intersection: calc(((var(--nova-font-scaling-min-width-rem) * -1) * var(--slope)) + var(--min-font-size-rem));
        --preferred-font-size: calc((var(--y-axis-intersection) * 1rem) + (var(--slope) * 100 * 1vw));
        --dynamic-font-size: round(clamp((var(--min-font-size-rem) * 1rem), var(--preferred-font-size), (var(--max-font-size-rem) * 1rem)), 1px);
      }

      .summary__terms h4 {
        font: var(--nova-h4-font);
        letter-spacing: var(--nova-h4-letter-spacing);
        text-transform: var(--nova-h4-text-transform);
        -webkit-font-smoothing: antialiased;
        /* These variables are used for dynamic font scaling. Learn more here – https://css-tricks.com/linearly-scale-font-size-with-css-clamp-based-on-the-viewport/ */
        --max-font-size-rem: var(--nova-h4-font-size) / var(--nova-base-font-size);
        --min-font-size-rem: var(--nova-h6-font-size) / var(--nova-base-font-size);
        --slope: calc((var(--max-font-size-rem) - var(--min-font-size-rem)) / (var(--nova-font-scaling-max-width-rem) - var(--nova-font-scaling-min-width-rem)));
        --y-axis-intersection: calc(((var(--nova-font-scaling-min-width-rem) * -1) * var(--slope)) + var(--min-font-size-rem));
        --preferred-font-size: calc((var(--y-axis-intersection) * 1rem) + (var(--slope) * 100 * 1vw));
        --dynamic-font-size: round(clamp((var(--min-font-size-rem) * 1rem), var(--preferred-font-size), (var(--max-font-size-rem) * 1rem)), 1px);
      }

      .summary__terms h5 {
        font: var(--nova-h5-font);
        letter-spacing: var(--nova-h5-letter-spacing);
        text-transform: var(--nova-h5-text-transform);
        -webkit-font-smoothing: antialiased;
      }

      .summary__terms h6 {
        font: var(--nova-h6-font);
        letter-spacing: var(--nova-h6-letter-spacing);
        text-transform: var(--nova-h6-text-transform);
        -webkit-font-smoothing: antialiased;
      }

      @media screen and (max-width: 749px) {
        .drawer::part(panel) {
          box-shadow: none;
        }
        .drawer::part(header) {
          padding-top: 37px;
          background: var(--nova-color-white);
          border-top: 0;
        }
        .drawer::part(title) {
          background: var(--nova-color-grey-100);
          padding: var(--nova-space-sm) var(--nova-space-md);
          box-shadow: 0 1px 6px 1px rgba(102, 102, 102, 0.10);
          border-top: 1px solid var(--nova-color-grey-200);
        }
        .drawer::part(header-actions) {
            top: 10px;
            right: 20px;
            padding: 0;
        }
      }

      @media screen and (min-width: 750px) {
        .drawer {
          --size: 454px;
          --body-spacing: 40px 20px;
        }
        .drawer::part(panel) {
          inset-inline-end: 20px;
          bottom: 25px;
          top: calc(var(--header-height) + var(--nova-space-xs));
          box-shadow: 0 0 10px 0 rgba(102, 102, 102, 0.25);
          border-radius: var(--nova-space-xs);
        }
        .drawer::part(header) {
          border: 0;
          padding: 20px 20px 15px 20px;
          background: var(--nova-color-grey-100);
          box-shadow: 0 1px 6px 1px rgba(102, 102, 102, 0.10);
        }
        .drawer::part(title) {
          gap: 10px;
        }
        .drawer::part(header-actions) {
          padding: 0;
          top: 20px;
          right: 30px;
        }
        .summary__terms {
          margin-top: var(--nova-space-sm);
        }
      }

      .w-full {
        width: 100%;
      }

      .btn {
        border-width: 1px;
        border-radius: var(--nova-border-rounded);
        height: 50px;
        display: inline-flex;
        gap: var(--nova-button-gap);
        flex-shrink: 0;
        justify-content: center;
        align-items: center;
        font: var(--nova-body-utility);
        letter-spacing: var(--nova-utility-letter-spacing);
        text-transform: var(--nova-utility-text-transform);
        transition: all var(--nova-button-animation-duration) var(--nova-animation-easing);
        text-decoration: none;
        cursor: pointer;
      }

      .btn[variant="primary-reverse"] {
        color: var(--nova-color-white);
        border-color: var(--nova-color-moink-maroon);
        background-color: var(--nova-color-moink-maroon);
      }

      .btn[variant="primary-reverse"]:not(:disabled):hover {
        color: var(--nova-color-white);
        border-color: var(--nova-color-moink-dark-grey);
        background-color: var(--nova-color-moink-dark-grey);
      }

      .btn[variant="primary-reverse"]:disabled {
        color: var(--nova-color-moink-dark-grey);
        border-color: var(--nova-color-disabled);
        background-color: var(--nova-color-disabled);
        cursor: default;
      }

      .btn[variant="primary-reverse"]:focus,
      .btn[variant="primary-reverse"]:focus-visible {
        color: var(--nova-color-white);
        border-color: var(--nova-color-moink-maroon);
        background-color: var(--nova-color-moink-maroon);
      }

      .btn[variant="primary-reverse"]:focus {
        outline: none;
      }

      .btn[variant="primary-reverse"]:focus-visible {
        outline: var(--nova-color-focus) solid 2px;
        outline-offset: 0;
      }

      .empty-content {
        margin-top: 30px;
        flex-direction: column;
        gap: var(--nova-space-xs);
        font: var(--nova-h6-font);
        letter-spacing: var(--nova-h6-letter-spacing);
        text-transform: var(--nova-h6-text-transform);
        -webkit-font-smoothing: antialiased;
        color: var(--nova-color-black);
        text-align: center;

        & p {
          margin: 0;
        }

        & a {
          color: var(--nova-color-black);
          text-underline-offset: 3px;
        }
      }
      .empty-btn {
        width: 288px;
      }
    `,
  ]
}

customElements.define('nova-mini-cart', NovaMiniCart)
